import { CheckIcon, CopyIcon, Loader2Icon, Volume2Icon, VolumeXIcon } from 'lucide-react';
import { useCallback, useEffect, useState, type ReactElement } from 'react';

import { Button } from '@/components/ui/button';

import { useChatActions } from '../hooks/useChatActions';
import { useChatStore } from '../ChatProvider';
import type { TextMessage } from '../state/types';

interface AnswerActionsProps {
  message: TextMessage;
  showPodcastCta: boolean;
  podcastEnabled: boolean;
  copyEnabled: boolean;
}

export function AnswerActions({
  message,
  showPodcastCta,
  podcastEnabled,
  copyEnabled,
}: AnswerActionsProps): ReactElement {
  const { generatePodcast } = useChatActions();
  const podcastStatus = useChatStore((s) => s.podcastGen[message.id] ?? 'idle');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => {
      setCopied(false);
    }, 2000);
    return () => {
      window.clearTimeout(id);
    };
  }, [copied]);

  const onCopy = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return;
    void navigator.clipboard.writeText(message.text).then(() => {
      setCopied(true);
    });
  }, [message.text]);

  const showCta = podcastEnabled && showPodcastCta && podcastStatus !== 'done';
  const isGenerating = podcastStatus === 'generating';

  return (
    <div className="flex items-center gap-1">
      {copyEnabled && (
        <Button
          type="button"
          size="xs"
          variant="ghost"
          aria-label={copied ? 'Mensagem copiada' : 'Copiar mensagem'}
          onClick={onCopy}
          disabled={copied}
          className="text-muted-foreground hover:text-foreground h-8 rounded-full px-3 text-[10.5px]"
        >
          {copied ? <CheckIcon aria-hidden /> : <CopyIcon aria-hidden />}
          {copied ? 'Copiado' : 'Copiar'}
        </Button>
      )}

      {showCta && (
        <Button
          type="button"
          size="xs"
          variant="ghost"
          aria-label={isGenerating ? 'Gerando podcast' : 'Gerar podcast'}
          onClick={() => {
            void generatePodcast(message.id);
          }}
          disabled={isGenerating}
          className="text-muted-foreground hover:text-foreground h-8 rounded-full px-3 text-[10.5px]"
        >
          {isGenerating ? (
            <Loader2Icon aria-hidden className="animate-spin" />
          ) : (
            <Volume2Icon aria-hidden />
          )}
          {isGenerating ? 'Gerando…' : 'Podcast'}
        </Button>
      )}

      {podcastEnabled && podcastStatus === 'done' && (
        <span className="text-muted-foreground inline-flex items-center gap-1 px-1 text-[10.5px]">
          <VolumeXIcon aria-hidden className="size-3" />
          Podcast gerado
        </span>
      )}
    </div>
  );
}
